<template>
  <v-layout row justify-center>
    <v-dialog v-model="dialogAuditItem" fullscreen transition="dialog-bottom-transition" scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.native="close">
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>{{$t('audititem_title')}}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark flat @click.native="save">{{$t('save')}}</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text>
          <v-container grid-list-md style="background-color: white;">
            <v-layout wrap>
              <v-flex xs12 sm6 md4 v-if="configuration.category">
                <v-autocomplete
                  @input="setCategory(editedItem.question.category)"
                  :items="currentCategories"
                  item-text="name"
                  item-value="id"
                  v-model="questionCategory"
                  :label="$t('category_label')"
                  hint="Kategorisierung für diese Frage"
                  persistent-hint
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.subcategory">
                <v-autocomplete
                  :items="currentSubcategories"
                  item-text="name"
                  item-value="id"
                  v-model="editedItem.question.subcategory"
                  :label="$t('subcategory_label')"
                  hint="Sub Kategorisierung für diese Frage"
                  persistent-hint
                  no-data-text="Keine Subkategorien vorhanden"
                >
                  <template slot="no-data">Keine Subkategorien vorhanden</template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.chapters">
                <v-autocomplete
                  :items="currentChapters"
                  item-value="id"
                  v-model="editedItem.question.chapters"
                  :label="$t('chapters_label')"
                  chips
                  multiple
                  hint="Relevante Normkapitel für diese Frage"
                  persistent-hint
                  return-object
                >
                  <template
                    slot="item"
                    slot-scope="item"
                  >{{ item.item.id }} - {{ item.item.description }}</template>

                  <template slot="selection" slot-scope="item">
                    <v-chip>
                      <div
                        :title="item.item.id +' - ' + item.item.description"
                      >{{ item.item.id }} - {{ item.item.description }}</div>
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.importance">
                <v-autocomplete
                  :items="importance"
                  item-value="id"
                  v-model="editedItem.question.importance"
                  :label="$t('importance_label')"
                  hint="Bedeutung dieses Kriteriums zum Bestehen des Audits"
                  persistent-hint
                >
                  <template
                    slot="item"
                    slot-scope="item"
                  >{{ item.item.id }} - {{ item.item.description }}</template>

                  <template
                    slot="selection"
                    slot-scope="item"
                  >{{ item.item.id }} - {{ item.item.description }}</template>
                </v-autocomplete>
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.processes">
                <v-autocomplete
                  :items="currentProcesses"
                  item-text="displayName"
                  item-value="displayName"
                  v-model="editedItem.processes"
                  :label="$t('processes_label')"
                  multiple
                  chips
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.questioning_date">
                <MenuDatePicker
                  :label="$t('auditing_date_label')"
                  :allowed-dates="allowedDates"
                  v-model="editedItem.questioning_date"
                  clearable
                />
              </v-flex>

              <v-flex xs12 sm6 md4 v-if="configuration.auditors">
                <v-autocomplete
                  :items="currentAuditors"
                  item-text="displayName"
                  item-value="id"
                  item-avatar="avatar"
                  v-model="editedItem.auditor"
                  :label="$t('auditor_label')"
                  multiple
                  chips
                ></v-autocomplete>
              </v-flex>

              <v-flex xs12 sm12 md12 v-if="configuration.question">
                <v-textarea
                  multi-line
                  v-model="editedItem.question.text"
                  :label="$t('audititem_label')"
                ></v-textarea>
              </v-flex>

              <v-flex xs12 sm12 md12 v-if="configuration.hint">
                <v-textarea
                  multi-line
                  v-model="editedItem.question.hint"
                  :label="$t('audititem_hint_label')"
                ></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click.native="close">{{ $t('cancel') }}</v-btn>
          <v-btn color="blue darken-1" flat @click.native="save">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<style>
.v-select__selections {
  overflow: hidden;
}
.v-chip {
  overflow: hidden;
  text-overflow: ellipsis;
}
.v-chip__content,
.v-chip__content div {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
<i18n>
{
  "de": {
    "audititem_title": "Audit-Frage",
    "audititem_label": "Audit-Frage",
    "audititem_hint_label": "Hinweis",
    "auditor_label": "Zugeordneter Auditor",
    "auditing_date_label": "Audit-Tag",
    "category_label": "Kategorie",
    "subcategory_label": "Unter-Kategorie",
    "chapters_label": "Zugeordnete Normkapitel",
    "importance_label": "Bedeutung",
    "processes_label": "Verlinkte Prozesse (Q.wiki)",
    "cancel": "Abbrechen",
    "save": "Speichern"
  },
  "en": {
    "audititem_title": "Audit Question",
    "audititem_label": "Audit Question",
    "audititem_hint_label": "Hint",
    "auditor_label": "Auditor",
    "auditing_date_label": "Audit day",
    "category_label": "Category",
    "subcategory_label": "Subcategory",
    "chapters_label": "Assigned Standard Chapters",
    "importance_label": "Importance",
    "processes_label": "Linked processes (Q.wiki)",
    "cancel": "Cancel",
    "save": "Save"   
  }
}
</i18n>

<script>
import _ from "lodash";

import { mapGetters, mapActions } from "vuex";
import { api as standardsapi } from "@/store/modules/standards";
import { api as confApi } from "@/store/modules/configuration";
import { api } from "@/store/modules/audits";
import { api as auditApi } from "@/store/modules/audit";
import { api as usersApi } from "@/store/modules/users";
import MenuDatePicker from "@/components/controls/MenuDatePicker";

export default {
  props: {
    dialogAuditItem: {
      default: {}
    }
  },
  components: { MenuDatePicker },
  data() {
    return {
      menu: false,
      today: null,
      currentCategory: ""
    };
  },
  computed: {
    ...mapGetters(standardsapi.namespace, [standardsapi.getters.getStandard]),
    ...mapGetters(confApi.namespace, [
      confApi.getters.audititem,
      confApi.getters.categories,
      confApi.getters.getCategory,
      confApi.getters.getSubcategory
    ]),
    ...mapGetters(api.namespace, [
      api.getters.getDialogItem,
      api.getters.getDialogAction,
      api.getters.getAuditId,
      api.getters.getAuditItemId,
      api.getters.loadedAudit
    ]),
    ...mapGetters(auditApi.namespace, [auditApi.getters.getAuditDocumentData]),

    ...mapGetters(usersApi.namespace, [usersApi.getters.getUser]),
    currentAudit() {
      return this.getAuditDocumentData;
    },
    configuration() {
      return this.audititem.dialog || {};
    },
    importance() {
      return this.audititem.data.importance || {};
    },
    currentChapters() {
      // TODO: Möglichkeit bei mehreren zu auditierenden Standards schaffen
      console.log("Standard?", this.currentAudit.standard);
      const standards = this.currentAudit.standard || "iso9001";

      const standard = this.getStandard(standards);
      console.assert(
        typeof standard === "object",
        "Expect valid standard object. Found",
        standard,
        "for Id",
        this.currentAudit.standard
      );
      console.log("Getting the Chapters", standard);
      let chapters = [];
      if (standard) {
        chapters = _.map(standard.chapters, chapter => {
          return { ...chapter, standard: standard.id };
        });
        console.log("Chapters", chapters);
      }

      return chapters;
    },
    currentAuditors() {
      const auditors = [...(this.currentAudit.coauditors || [])];
      auditors.push(this.currentAudit.leadauditor);
      return auditors.map(v => {
        return this.getUser(v);
      });
    },
    currentAuditDates() {
      const auditdates = this.currentAudit.auditing_date;
      console.log("Audit Datum", auditdates);
      return auditdates;
    },
    questionCategory: {
      get() {
        if (typeof this.editedItem.question.category === "string") {
          return [this.editedItem.question.category];
        } else {
          return this.editedItem.question.category;
        }
      },
      set(v) {
        this.editedItem.question.category = v;
      }
    },
    // TODO: Kategorien und Subcategorien sind vermutlich eng mit dem genutzten Standard verbunden
    currentCategories() {
      //return this.audititem.data.categories || [];

      let standard = this.currentAudit.standard;
      console.log("Get Categories" + standard);

      // TODO: SMELL
      if (standard instanceof Array && standard.length) {
        standard = standard[0];
      }

      const categories = this.getCategory(standard);

      console.log("categories", categories);

      const flatter = (categories, parent) => {
        let result = [];
        for (const category of categories) {
          result.push({
            id: [...parent, category.id],
            name:
              String.fromCodePoint(0x00a0).repeat(parent.length * 2) +
              category.name
          });
          if ("children" in category) {
            {
              result = result.concat(
                flatter(category.children, [...parent, category.id])
              );
            }
          }
        }
        return result;
      };

      const flattend_categories = flatter(categories, []);
      console.log("Flattend", flattend_categories);

      return flattend_categories || [];
    },
    currentSubcategories() {
      if (this.currentCategory) {
        let category = this.currentCategories.find(category => {
          return category.id === this.currentCategory;
        });

        return category.subcategories || [];
      } else {
        return [];
      }
    },
    editedItem() {
      return this.getDialogItem;
    },
    isUpdate() {
      if (this.getDialogAction === "update") {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    ...mapActions(api.namespace, [
      api.actions.createAuditItem,
      api.actions.updateAuditItem
    ]),
    setCategory(val) {
      this.currentCategory = val || "";
    },
    allowedDates(val) {
      let el = this.currentAuditDates || [];
      if (el.indexOf(val) !== -1) {
        return true;
      } else {
        return false;
      }
    },
    close() {
      this.$emit("update:dialogAuditItem", false);
    },
    save() {
      if (this.isUpdate) {
        console.log("Updating Audit-Item");
        const audit_item = { ...this.editedItem };

        const payload = {
          audit_id: this.getAuditId,
          audititem_id: this.getAuditItemId,
          data: audit_item
        };

        this.updateAuditItem(payload);
      } else {
        console.log("Creating new Audit-Item");
        const audit_item = [{ ...this.editedItem }];

        const payload = {
          audit_id: this.getAuditId,
          data: audit_item
        };

        this.createAuditItem(payload);
      }

      this.close();
    }
  }
};
</script>
